/**
 * HTTP Client
 *
 * A fetch-based HTTP client that attaches a fresh SSO access token to
 * every request. Silent token refresh is handled by the SSO provider
 * (see src/auth/sso.ts) — no manual refresh-token plumbing needed.
 *
 * USAGE:
 * ```typescript
 * import { httpClient } from '@/utils/httpClient';
 *
 * const response = await httpClient.get<User[]>('/users');
 * const newUser = await httpClient.post<User>('/users', { name: 'John' });
 * ```
 */

import { API_CONFIG } from '@/config/constants';
import { getSsoToken } from '@/auth/sso';
import { clearAuthData } from './auth';

/**
 * HTTP request options
 */
interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  skipAuth?: boolean;
}

/**
 * HTTP response wrapper
 */
interface HttpResponse<T> {
  data: T;
  status: number;
  ok: boolean;
}

/**
 * Custom HTTP Error class
 */
export class HttpError extends Error {
  status: number;
  data: unknown;

  constructor(message: string, status: number, data?: unknown) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
    this.data = data;
  }
}

/**
 * Make an HTTP request with automatic token handling
 */
const request = async <T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<HttpResponse<T>> => {
  const { body, skipAuth = false, headers: customHeaders, ...restOptions } = options;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(customHeaders as Record<string, string> | undefined),
  };

  if (!skipAuth) {
    const token = await getSsoToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  const config: RequestInit = {
    ...restOptions,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  };

  const url = endpoint.startsWith('http')
    ? endpoint
    : `${API_CONFIG.BASE_URL}${endpoint}`;

  const response = await fetch(url, config);

  // Session is gone and could not be refreshed — clear the mirror and
  // let react-admin's checkError redirect to the portal.
  if (response.status === 401 && !skipAuth) {
    clearAuthData();
    throw new HttpError('Session expired', 401, { authError: true });
  }

  // Parse response
  let data: T;
  const contentType = response.headers.get('content-type');

  if (contentType?.includes('application/json')) {
    data = (await response.json()) as T;
  } else {
    data = (await response.text()) as unknown as T;
  }

  if (!response.ok) {
    throw new HttpError(
      (data as { message?: string })?.message || response.statusText,
      response.status,
      data
    );
  }

  return {
    data,
    status: response.status,
    ok: response.ok,
  };
};

/**
 * HTTP client with convenience methods
 */
export const httpClient = {
  get: <T>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'GET' }),

  post: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'POST', body }),

  put: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'PUT', body }),

  patch: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'PATCH', body }),

  delete: <T>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'DELETE' }),
};

export default httpClient;
